import { Component, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material';
import { ButtonTriggerEvent } from '../../../../common/components';
import { BaseComponent } from '../../../../common/components/base.component';
import { SuccessWrapComponent } from '../../../../common/components/success.wrap/success.wrap.component';
import { AccountApiService } from '../../../../services/account.api.service';
import { SetContactsModalData } from './set.contacts.modal.provider';
import { AlertifyService } from '../../../../services/alertify.service';

@Component({
  templateUrl: './set.contacts.modal.component.html',
  styleUrls: ['./set.contacts.modal.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class SetContactsModalComponent extends BaseComponent {
  private static readonly SSR_DETAIL_TOGGLE_ROLLOUT_HUBS = [
    'randy',
    'savannah2',
    'testwix',
    'newsmyrnabeach',
    'adventurecoast',
    'panamacityfl',
  ];
  public hubContactsForm: FormGroup;
  public placeForm: FormGroup;

  public contact: {
    name: '';
    number: '';
    email: '';
    inquiryEmail: '';
    eventsSsrEnabled: false;
    placesSsrEnabled: false;
    eventCalendarURL: '';
    placeCalendarURL: '';
    moreToursURL: '';
    placeURLs: { igURL; clientURL }[];
  };
  public webhookSettings: {
    enabled: boolean;
    endpointUrl: string;
    signingSecret: string;
    sendEventDetailInvalidation: boolean;
    sendEventListingInvalidation: boolean;
    sendPlaceDetailInvalidation: boolean;
    sendPlaceListingInvalidation: boolean;
    sendItineraryDetailInvalidation: boolean;
    sendItineraryListingInvalidation: boolean;
    sendSsrModuleStatusUpdate: boolean;
  };
  public account: SetContactsModalData;
  @ViewChild('success') public success: SuccessWrapComponent;

  get shouldShowSsrDetailAvailabilityToggles() {
    const hubName = `${(this.account && this.account.account && this.account.account.userName) || ''}`
      .trim()
      .toLowerCase();
    return SetContactsModalComponent.SSR_DETAIL_TOGGLE_ROLLOUT_HUBS.indexOf(hubName) !== -1;
  }

  constructor(
    private fb: FormBuilder,
    private accountApiService: AccountApiService,
    private alertify: AlertifyService,
    private dialogRef: MatDialogRef<SetContactsModalComponent>,
    data: SetContactsModalData
  ) {
    super();
    this.account = Object.assign({}, data);
    this.ensureState();
  }

  ngOnInit() {
    this.hubContactsForm = this.fb.group({
      name: [null, [Validators.required]],
      number: [null],
      email: [null, [Validators.required]],
      inquiryEmail: [null, null],
      event: [null, null],
      place: [null, null],
      tour: [null, null],
      webhookUrl: [null, null],
      signingSecret: [null, null],
    });

    this.placeForm = this.fb.group({
      dynamicFields: this.fb.array([]),
    });

    for (const url of this.contact.placeURLs || []) {
      this.addPlaceURL(url.igURL, url.clientURL);
    }
  }

  close() {
    this.dialogRef.close();
  }

  public save(event: ButtonTriggerEvent) {
    const array = this.placeForm.get('dynamicFields') as FormArray;
    console.log(array);
    const urls = array.value.filter((x) => x.igURL && x.clientURL);
    const igDegaulePlaceCategories = [
      'All',
      'Accommodations',
      'Activities',
      'Arts & Culture',
      'Dining & Nightlife',
      'Entertainment',
      'Outdoors',
      'Wellness',
      'Shopping',
      'Transportation',
      'Other',
    ];

    for (const url of urls) {
      const regex = /\/places\/(?:\w+\/custom|fav\/[a-fA-F0-9]{24})(?:\/|$)/;
      const match = url.igURL.match(regex);
      if (!match && !igDegaulePlaceCategories.any((x) => x === url.igURL)) {
        this.alertify.warning(
          `ImGoing place URL must be ImGoing's default place category (
            ${igDegaulePlaceCategories.join(', ')}). 
            <br/><br/>Or custom category in this format: /${
              this.account.account.userName
            }/places/664b3fdcbf671088a01a8322/custom
            <br/><br/>Or favorite listing in this format: /${
              this.account.account.userName
            }/places/fav/5fb43db62669327fb0615d79`,
          10
        );
        setTimeout(() => {
          const msgbox = document.querySelector('.ajs-message');
          if (msgbox) {
            (msgbox as HTMLElement).style.width = '450px';
            (msgbox as HTMLElement).style.right = '490px';
          }
        });
        event.immediateComplete();
        return;
      }
    }

    this.contact.placeURLs = urls;
    this.webhookSettings.endpointUrl = (this.webhookSettings.endpointUrl || '').trim();
    this.webhookSettings.signingSecret = (this.webhookSettings.signingSecret || '').trim();

    if (this.webhookSettings.enabled) {
      if (!this.webhookSettings.endpointUrl) {
        this.alertify.warning('Webhook URL is required when webhook delivery is enabled.');
        event.immediateComplete();
        return;
      }

      if (!/^https?:\/\//i.test(this.webhookSettings.endpointUrl)) {
        this.alertify.warning('Webhook URL must start with http:// or https://');
        event.immediateComplete();
        return;
      }

      if (!this.webhookSettings.signingSecret) {
        this.alertify.warning('Signing secret is required when webhook delivery is enabled.');
        event.immediateComplete();
        return;
      }

      if (
        !this.webhookSettings.sendEventDetailInvalidation &&
        !this.webhookSettings.sendEventListingInvalidation &&
        !this.webhookSettings.sendPlaceDetailInvalidation &&
        !this.webhookSettings.sendPlaceListingInvalidation &&
        !this.webhookSettings.sendItineraryDetailInvalidation &&
        !this.webhookSettings.sendItineraryListingInvalidation &&
        !this.webhookSettings.sendSsrModuleStatusUpdate
      ) {
        this.alertify.warning('Select at least one webhook subscription before enabling webhook delivery.');
        event.immediateComplete();
        return;
      }
    }

    this.accountApiService
      .setContacts(this.account.account._id, {
        contacts: this.contact,
        webhookSettings: {
          ...(this.account.account.webhookSettings || {}),
          apiUserCacheWebhook: this.webhookSettings,
        },
      })
      .finally(event.complete)
      .subscribe((data) => {
        this.success.success();

        setTimeout(() => {
          this.dialogRef.close();
        }, 2000);
      });
  }

  public addPlaceURL(igURL = '', clientURL = '') {
    const newGroup = this.fb.group({
      igURL: [igURL],
      clientURL: [clientURL],
    });
    (this.placeForm.get('dynamicFields') as FormArray).push(newGroup);
  }

  public getControls() {
    const field = this.placeForm.get('dynamicFields');
    if (field) {
      return (field as any).controls;
    }
    return [];
  }

  public generateWebhookSecret() {
    const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let secret = '';
    for (let i = 0; i < 48; i++) {
      secret += alphabet.charAt(Math.floor(Math.random() * alphabet.length));
    }
    this.webhookSettings.signingSecret = secret;
  }

  private ensureState() {
    const account: any = this.account.account || {};
    const contacts = account.contacts || {};
    this.contact = {
      name: contacts.name || '',
      number: contacts.number || '',
      email: contacts.email || '',
      inquiryEmail: contacts.inquiryEmail || '',
      eventsSsrEnabled: !!contacts.eventsSsrEnabled,
      placesSsrEnabled: !!contacts.placesSsrEnabled,
      eventCalendarURL: contacts.eventCalendarURL || '',
      placeCalendarURL: contacts.placeCalendarURL || '',
      moreToursURL: contacts.moreToursURL || '',
      placeURLs: Array.isArray(contacts.placeURLs) ? contacts.placeURLs : [],
    } as any;
    account.contacts = this.contact;

    const webhookSettingsContainer = account.webhookSettings || {};
    const webhookSettings =
      webhookSettingsContainer.apiUserCacheWebhook || webhookSettingsContainer;

    this.webhookSettings = {
      enabled: !!webhookSettings.enabled,
      endpointUrl: webhookSettings.endpointUrl || '',
      signingSecret: webhookSettings.signingSecret || '',
      sendEventDetailInvalidation:
        typeof webhookSettings.sendEventDetailInvalidation === 'undefined'
          ? true
          : !!webhookSettings.sendEventDetailInvalidation,
      sendEventListingInvalidation:
        typeof webhookSettings.sendEventListingInvalidation === 'undefined'
          ? true
          : !!webhookSettings.sendEventListingInvalidation,
      sendPlaceDetailInvalidation:
        typeof webhookSettings.sendPlaceDetailInvalidation === 'undefined'
          ? true
          : !!webhookSettings.sendPlaceDetailInvalidation,
      sendPlaceListingInvalidation:
        typeof webhookSettings.sendPlaceListingInvalidation === 'undefined'
          ? true
          : !!webhookSettings.sendPlaceListingInvalidation,
      sendItineraryDetailInvalidation:
        typeof webhookSettings.sendItineraryDetailInvalidation === 'undefined'
          ? true
          : !!webhookSettings.sendItineraryDetailInvalidation,
      sendItineraryListingInvalidation:
        typeof webhookSettings.sendItineraryListingInvalidation === 'undefined'
          ? true
          : !!webhookSettings.sendItineraryListingInvalidation,
      sendSsrModuleStatusUpdate:
        typeof webhookSettings.sendSsrModuleStatusUpdate === 'undefined'
          ? true
          : !!webhookSettings.sendSsrModuleStatusUpdate,
    };
    account.webhookSettings = {
      ...webhookSettingsContainer,
      apiUserCacheWebhook: this.webhookSettings,
    };
  }
}



// WEBPACK FOOTER //
// D:/Work/Development/ImGoing/IMGoing/web/src/components/admin/accounts/set.contacts/set.contacts.modal.component.ts